
import { formatW, recommendSupply, SMALLEST_HIRE_KVA } from '@/lib/power'
import type { BuiltPowerPlan } from '@/lib/power-plan'
import { card, cx, notice } from '@/lib/ui'

/**
 * The whole point of the page, in one block.
 *
 * A technical director needs two sentences before a job: which generator to
 * hire, and what to ask the venue for. Everything else this app can work out —
 * circuits, legs, phase balance — is an electrician's problem, and burying
 * these two answers underneath it helped nobody.
 *
 * Sized on the peak, everything on at once. Under-ordering costs a show;
 * over-ordering costs a slightly larger set at the same day rate.
 */
export function PowerAnswer({ built }: { built: BuiltPowerPlan }) {
  const rec = recommendSupply(built.totals.vaConnected, built.totals.wDemand)
  const unknown = built.coverage.unknown
  const empty = built.rows.length === 0

  return (
    <section className={cx(card, 'px-6 py-6')} aria-label="Power estimate">
      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted">
        Estimated draw
      </p>
      <p className="mt-1 text-4xl font-semibold tabular-nums tracking-tight">
        {empty ? '—' : formatW(built.totals.wDemand)}
      </p>
      <p className="mt-1 text-[11px] text-muted">
        everything on at once · {built.rows.length} {built.rows.length === 1 ? 'item' : 'items'}
      </p>

      {!empty && (
        <div className="mt-5 grid gap-3 border-t border-[var(--border)] pt-5 sm:grid-cols-2">
          <Recommendation
            label="If you're on a generator"
            // Below the smallest set anyone hires, naming a size invites
            // someone to go shopping for a 10 kVA generator that does not
            // exist on a hire list. The useful answer is "don't worry".
            value={
              rec.anyHireSetCovers
                ? 'Any hire set'
                : rec.gensetKva
                  ? `${rec.gensetKva} kVA set`
                  : 'Bigger than a standard set'
            }
            note={
              rec.gensetKva === null
                ? 'Past the biggest set on the usual hire list — talk to your supplier.'
                : rec.anyHireSetCovers
                  ? `This show draws far less than the smallest set anyone sends — about ${SMALLEST_HIRE_KVA} kVA. Nothing to size.`
                  : 'Ask for this size or larger.'
            }
          />
          <Recommendation
            label="If you're on building power"
            value={`Ask for ${rec.buildingAskKva} kVA`}
            note="What to put in the request to the venue's engineer."
          />
        </div>
      )}

      {/* The only caveat that changes what someone does, and it changes the
          number rather than qualifying it. */}
      {unknown > 0 && (
        <p className={cx(notice.warning, 'mt-5')}>
          {unknown} {unknown === 1 ? 'item has' : 'items have'} no power figure, so{' '}
          {unknown === 1 ? 'it is' : 'they are'} not counted above — this could be low by roughly{' '}
          <strong className="font-medium">{formatW(built.coverage.unknownCouldAddW)}</strong>. Fill{' '}
          {unknown === 1 ? 'it' : 'them'} in below and the estimate updates.
        </p>
      )}
    </section>
  )
}

function Recommendation({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-xl bg-[var(--surface-raised)]/70 px-4 py-3">
      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums tracking-tight">{value}</p>
      <p className="mt-1 text-[11px] leading-relaxed text-muted">{note}</p>
    </div>
  )
}
